import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { Briefcase, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerLogin = ({ onBack, onLoginSuccess }) => {
  const { t } = useLanguage();
  const [loginData, setLoginData] = useState({
    email: '',
    partnerId: ''
  });
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'partnerId'
  const [isLoading, setIsLoading] = useState(false);
  const [showPartnerId, setShowPartnerId] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let partner = null;

      if (loginMethod === 'email') {
        if (!loginData.email) {
          toast({
            title: t('partner.login.error.notFound', 'Gabim'),
            description: t('partner.login.error.email', 'Ju lutemi vendosni email-in tuaj.'),
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Search for partner by email
        const response = await axios.get(`${API}/partners`);
        const partners = response.data;
        partner = partners.find(p => p.email.toLowerCase() === loginData.email.toLowerCase());

        if (!partner) {
          toast({
            title: t('partner.login.error.notFound', 'Partner nuk u gjet'),
            description: t('partner.login.error.emailNotFound', 'Nuk u gjet asnjë partner me këtë email. Ju lutemi regjistrohuni si partner i ri.'),
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      } else {
        if (!loginData.partnerId) {
          toast({
            title: t('partner.login.error.notFound', 'Gabim'),
            description: t('partner.login.error.partnerId', 'Ju lutemi vendosni ID-në tuaj të partnerit.'),
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        // Get partner by ID
        try {
          const response = await axios.get(`${API}/partners/${loginData.partnerId}`);
          partner = response.data;
        } catch (error) {
          if (error.response?.status === 404) {
            toast({
              title: t('partner.login.error.notFound', 'Partner nuk u gjet'),
              description: t('partner.login.error.idNotFound', 'Nuk u gjet asnjë partner me këtë ID. Ju lutemi kontrolloni ID-në.'),
              variant: "destructive",
            });
          } else {
            toast({
              title: t('partner.login.error.notFound', 'Gabim'),
              description: t('partner.login.error.general', 'Ndodhi një gabim gjatë kërkimit të partnerit.'),
              variant: "destructive",
            });
          }
          setIsLoading(false);
          return;
        }
      }

      if (partner) {
        toast({
          title: t('partner.login.welcome', 'Mirë se erdhët!'),
          description: t('partner.login.welcomeMessage', 'Mirë se erdhët, {name}!').replace('{name}', `${partner.firstName} ${partner.lastName}`),
        });
        onLoginSuccess(partner.id);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: t('partner.login.error.notFound', 'Gabim'),
        description: t('partner.login.error.general', 'Ndodhi një gabim gjatë hyrjes. Ju lutemi provoni përsëri.'),
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-md mx-auto">
          
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={onBack}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('partner.login.back', 'Kthehu')}
          </Button>

          {/* Login Card */}
          <Card className="shadow-xl">
            <CardHeader className="text-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
              <div className="bg-white/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Briefcase className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">{t('partner.login.title', 'Kyçuni si Partner')}</CardTitle>
              <p className="text-emerald-100 mt-2">
                {t('partner.login.subtitle', 'Hyni në llogarinë tuaj të partnerit')}
              </p>
            </CardHeader>

            <CardContent className="p-8">
              
              {/* Login Method Selection */}
              <div className="mb-6">
                <Label className="text-base font-semibold text-gray-900 block mb-3">
                  {t('partner.login.method', 'Si doni të hyni?')}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={loginMethod === 'email' ? 'default' : 'outline'}
                    onClick={() => setLoginMethod('email')}
                    className="text-sm"
                  >
                    {t('partner.login.email', 'Me Email')}
                  </Button>
                  <Button
                    type="button"
                    variant={loginMethod === 'partnerId' ? 'default' : 'outline'}
                    onClick={() => setLoginMethod('partnerId')}
                    className="text-sm"
                  >
                    {t('partner.login.partnerId', 'Me Partner ID')}
                  </Button>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                
                {/* Email Login */}
                {loginMethod === 'email' && (
                  <div>
                    <Label htmlFor="email">{t('partner.login.emailLabel', 'Adresa Elektronike')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder={t('partner.login.emailPlaceholder', 'partneri@shembull.com')}
                      className="mt-1"
                      required
                    />
                    <p className="text-sm text-gray-600 mt-1">
                      {t('partner.login.emailHelp', 'Vendosni email-in që përdorët gjatë regjistrimit')}
                    </p>
                  </div>
                )}

                {/* Partner ID Login */}
                {loginMethod === 'partnerId' && (
                  <div>
                    <Label htmlFor="partnerId">{t('partner.login.partnerIdLabel', 'Partner ID')} *</Label>
                    <div className="relative">
                      <Input
                        id="partnerId"
                        type={showPartnerId ? "text" : "password"}
                        value={loginData.partnerId}
                        onChange={(e) => setLoginData(prev => ({ ...prev, partnerId: e.target.value }))}
                        placeholder={t('partner.login.partnerIdPlaceholder', 'Vendosni Partner ID-në tuaj')}
                        className="mt-1 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPartnerId(!showPartnerId)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPartnerId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('partner.login.partnerIdHelp', 'Partner ID-në tuaj e gjeni në email-in e konfirmimit')}
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3"
                >
                  {isLoading ? t('partner.login.submitting', 'Duke u kyçur...') : t('partner.login.submit', 'Kyçuni')}
                </Button>
              </form>

              {/* Help Section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">{t('partner.login.helpTitle', 'Nuk mund të hyni?')}</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• {t('partner.login.helpId', 'Partner ID-në tuaj e gjeni në email-in e regjistrimit')}</p>
                  <p>• {t('partner.login.helpContact', 'Nëse keni harruar të dhënat, kontaktoni në +41 76 766 94 30')}</p>
                  <p>• {t('partner.login.helpRegister', 'Nëse nuk jeni partner ende, regjistrohuni si partner i ri')}</p>
                </div>
                
                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    onClick={onBack}
                    className="w-full"
                  >
                    {t('partner.login.registerButton', 'Kthehu për të u regjistruar si Partner')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;